import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  title?: string;
  duration?: number; // ms, default 4000
}

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration: number;
}

interface ToastContextType {
  toasts: ToastItem[];
  addToast: (type: ToastType, message: string, options?: ToastOptions) => string;
  removeToast: (id: string) => void;
  success: (message: string, options?: ToastOptions) => string;
  error: (message: string, options?: ToastOptions) => string;
  info: (message: string, options?: ToastOptions) => string;
  warning: (message: string, options?: ToastOptions) => string;
}

const ToastContext = createContext<ToastContextType | null>(null);

// Global event listener for toast outside React components
type ToastListener = (type: ToastType, message: string, options?: ToastOptions) => void;
const toastListeners = new Set<ToastListener>();

export const toast = {
  success: (message: string, options?: ToastOptions) => {
    toastListeners.forEach((listener) => listener('success', message, options));
  },
  error: (message: string, options?: ToastOptions) => {
    toastListeners.forEach((listener) => listener('error', message, options));
  },
  info: (message: string, options?: ToastOptions) => {
    toastListeners.forEach((listener) => listener('info', message, options));
  },
  warning: (message: string, options?: ToastOptions) => {
    toastListeners.forEach((listener) => listener('warning', message, options));
  },
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // Fallback to global toast methods if used outside provider
    return toast;
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: ToastType, message: string, options?: ToastOptions) => {
      const id = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
      const duration = options?.duration ?? 4000;

      const newToast: ToastItem = {
        id,
        type,
        message,
        title: options?.title,
        duration,
      };

      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    [],
  );

  useEffect(() => {
    const handleGlobalToast: ToastListener = (type, message, options) => {
      addToast(type, message, options);
    };

    toastListeners.add(handleGlobalToast);
    return () => {
      toastListeners.delete(handleGlobalToast);
    };
  }, [addToast]);

  const success = useCallback(
    (message: string, options?: ToastOptions) => addToast('success', message, options),
    [addToast],
  );
  const error = useCallback(
    (message: string, options?: ToastOptions) => addToast('error', message, options),
    [addToast],
  );
  const info = useCallback(
    (message: string, options?: ToastOptions) => addToast('info', message, options),
    [addToast],
  );
  const warning = useCallback(
    (message: string, options?: ToastOptions) => addToast('warning', message, options),
    [addToast],
  );

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, success, error, info, warning }}
    >
      {children}
      {/* Toast Container */}
      <div
        aria-live="polite"
        className="fixed top-5 right-5 z-[9999] flex flex-col gap-2.5 max-w-sm sm:max-w-md w-full pointer-events-none px-3"
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} item={t} onClose={() => removeToast(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

interface ToastCardProps {
  item: ToastItem;
  onClose: () => void;
}

const ToastCard: React.FC<ToastCardProps> = ({ item, onClose }) => {
  useEffect(() => {
    if (item.duration <= 0) return;
    const timer = setTimeout(() => {
      onClose();
    }, item.duration);

    return () => clearTimeout(timer);
  }, [item.duration, onClose]);

  const config = {
    success: {
      border: 'border-emerald-500/40 bg-[#0f172a]/95 text-emerald-300 shadow-emerald-950/30',
      icon: <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />,
      progressBar: 'bg-emerald-500',
      defaultTitle: 'Thành công',
    },
    error: {
      border: 'border-red-500/40 bg-[#0f172a]/95 text-red-300 shadow-red-950/30',
      icon: <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />,
      progressBar: 'bg-red-500',
      defaultTitle: 'Đã có lỗi xảy ra',
    },
    info: {
      border: 'border-blue-500/40 bg-[#0f172a]/95 text-blue-300 shadow-blue-950/30',
      icon: <Info className="h-5 w-5 text-blue-400 shrink-0" />,
      progressBar: 'bg-blue-500',
      defaultTitle: 'Thông tin hệ thống',
    },
    warning: {
      border: 'border-amber-500/40 bg-[#0f172a]/95 text-amber-300 shadow-amber-950/30',
      icon: <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />,
      progressBar: 'bg-amber-500',
      defaultTitle: 'Lưu ý',
    },
  }[item.type];

  return (
    <div
      role="alert"
      className={`pointer-events-auto relative overflow-hidden rounded-2xl border shadow-2xl backdrop-blur-xl p-4 transition-all duration-300 animate-in fade-in slide-in-from-top-4 ${config.border}`}
    >
      <div className="flex items-start gap-3">
        <div className="pt-0.5">{config.icon}</div>
        <div className="flex-1 pr-2">
          <h4 className="text-xs font-bold text-white tracking-wide">
            {item.title || config.defaultTitle}
          </h4>
          <p className="mt-1 text-xs text-slate-200 leading-relaxed break-words">{item.message}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800/60 transition-colors shrink-0"
          aria-label="Đóng thông báo"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Countdown progress bar */}
      {item.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800/80 overflow-hidden">
          <div
            className={`h-full ${config.progressBar}`}
            style={{
              animation: `shrinkWidth ${item.duration}ms linear forwards`,
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes shrinkWidth {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};
